import Image from 'next/image';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import PageLink from '@/utils/PageLink';

const useCases = [
  {
    title: 'Education & Academia',
    description:
      '24/7 student support, accreditation compliance, and institutional knowledge—all with cited sources.',
    image: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=800&q=80',
    gradient: 'from-blue-500 to-indigo-600',
    keyBenefits: [
      'Instant answers to course questions',
      'Accreditation audit support',
      'Research database navigation',
    ],
    metric: '85% fewer support tickets',
  },
  {
    title: 'Legal & Law Firms',
    description:
      'Search case precedents, collaborate on complex matters, and access firm knowledge instantly.',
    image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&q=80',
    gradient: 'from-amber-500 to-orange-600',
    keyBenefits: ['Case research with citations', 'Cross-team collaboration', 'Brief repository'],
    metric: '60% faster research',
  },
  {
    title: 'Healthcare & Medical',
    description:
      'Clinical protocols, HIPAA compliance, and treatment guidelines—verified and cited instantly.',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80',
    gradient: 'from-emerald-500 to-teal-600',
    keyBenefits: [
      'Treatment protocol verification',
      'HIPAA compliance guidance',
      'Drug interaction lookups',
    ],
    metric: '40% faster protocol lookup',
  },
  {
    title: 'Financial Services',
    description:
      'Navigate SEC, FINRA, and banking regulations with confidence using cited compliance guidance.',
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80',
    gradient: 'from-violet-500 to-purple-600',
    keyBenefits: [
      'Real-time regulatory guidance',
      'Product knowledge base',
      'Automated compliance checks',
    ],
    metric: '95% compliance accuracy',
  },
  {
    title: 'Energy & Utilities',
    description:
      'Master PUC regulations, rate case filings, and operational procedures across jurisdictions.',
    image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800&q=80',
    gradient: 'from-yellow-500 to-orange-600',
    keyBenefits: ['PUC regulatory guidance', 'Rate filing database', 'Safety protocols access'],
    metric: '70% faster research',
  },
  {
    title: 'Technology & SaaS',
    description:
      'Scale support and onboarding with AI that knows your docs, APIs, and codebase inside-out.',
    image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&q=80',
    gradient: 'from-cyan-500 to-blue-600',
    keyBenefits: [
      'Self-service technical support',
      'API documentation lookup',
      'Developer onboarding',
    ],
    metric: '3x faster resolution',
  },
  {
    title: 'Government',
    description:
      'Empower citizens and staff with instant access to policies, permits, and public service info.',
    image: 'https://images.unsplash.com/photo-1479142506502-19b3a3b7ff33?w=800&q=80',
    gradient: 'from-slate-500 to-gray-700',
    keyBenefits: [
      '24/7 permit information',
      'Policy guidance for staff',
      'Statute & ordinance search',
    ],
    metric: '50% fewer calls',
  },
  {
    title: 'Non-Profit & Associations',
    description:
      'Donor information, program details, and volunteer coordination—accessible to all staff instantly.',
    image: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=800&q=80',
    gradient: 'from-rose-500 to-pink-600',
    keyBenefits: [
      'Donor history lookup',
      'Program guidelines access',
      'Volunteer onboarding support',
    ],
    metric: '60% faster donor inquiries',
  },
  {
    title: 'Retail & E-Commerce',
    description:
      'Product knowledge, customer service automation, and inventory intelligence for seamless operations.',
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&q=80',
    gradient: 'from-fuchsia-500 to-purple-600',
    keyBenefits: ['24/7 product information', 'Order policy guidance', 'Supplier data access'],
    metric: '4x more self-service',
  },
  {
    title: 'Insurance',
    description:
      'Claims processing, policy interpretation, and regulatory compliance with instant cited answers.',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80',
    gradient: 'from-sky-500 to-blue-600',
    keyBenefits: ['Faster claims resolution', 'Policy knowledge base', 'Compliance automation'],
    metric: '65% faster claims',
  },
  {
    title: 'Construction & Engineering',
    description:
      'Building codes, safety regulations, and project specifications with verifiable sources.',
    image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&q=80',
    gradient: 'from-orange-500 to-red-600',
    keyBenefits: ['Code compliance verification', 'Safety protocol lookup', 'Spec sheet access'],
    metric: '45% fewer compliance issues',
  },
  {
    title: 'Hospitality & Tourism',
    description:
      'Guest services, booking policies, and local recommendations—always accurate, always cited.',
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
    gradient: 'from-teal-500 to-cyan-600',
    keyBenefits: [
      'Instant booking policy answers',
      'Local attraction information',
      'Service protocol access',
    ],
    metric: '90% guest satisfaction',
  },
];

const UseCases = () => {
  return (
    <div>
      <NextSeo
        title="Industry Use Cases - AI Knowledge Agents"
        description="Discover how organizations across Education, Legal, Healthcare, Finance, Energy, Technology, and Government use AI knowledge agents to transform operations with cited, verifiable answers."
        canonical="https://chat.co/use-cases"
        openGraph={{
          url: 'https://chat.co/use-cases',
          title: 'Industry Use Cases - AI Knowledge Agents | Chat.co',
          description:
            'See how leading organizations leverage AI-powered knowledge agents to accelerate decisions, ensure compliance, and empower teams.',
          images: [
            {
              url: 'https://chat.co/images/og-image.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co Industry Use Cases',
            },
          ],
        }}
      />

      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-gray-50 to-white px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-7xl text-center">
          <span className="inline-flex items-center rounded-full bg-primary-50 px-4 py-2 text-sm font-medium text-primary-700">
            INDUSTRY SOLUTIONS
          </span>
          <h1 className="mt-6 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
            AI Knowledge Agents
            <span className="mt-2 block">Across Every Industry</span>
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-lg text-gray-600 sm:text-xl">
            See how organizations transform operations with verifiable, cited answers from their own
            knowledge base—reducing search time, ensuring compliance, and empowering teams.
          </p>
        </div>
      </section>

      {/* Use Cases Grid */}
      <section className="bg-white px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {useCases.map((useCase) => {
              const slug = useCase.title.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-');
              let href = '#';
              if (useCase.title === 'Education & Academia') href = '/use-cases/education';
              if (useCase.title === 'Legal & Law Firms') href = '/use-cases/legal';
              if (useCase.title === 'Healthcare & Medical') href = '/use-cases/healthcare';
              if (useCase.title === 'Financial Services') href = '/use-cases/financial-services';
              if (useCase.title === 'Energy & Utilities') href = '/use-cases/energy';
              if (useCase.title === 'Technology & SaaS') href = '/use-cases/technology';
              if (useCase.title === 'Government') href = '/use-cases/government';
              if (useCase.title === 'Non-Profit & Associations') href = '/use-cases/nonprofit';
              if (useCase.title === 'Retail & E-Commerce') href = '/use-cases/retail';
              if (useCase.title === 'Insurance') href = '/use-cases/insurance';
              if (useCase.title === 'Construction & Engineering') href = '/use-cases/construction';
              if (useCase.title === 'Hospitality & Tourism') href = '/use-cases/hospitality';

              return (
                <Link key={useCase.title} href={href}>
                  <article className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white transition-all hover:shadow-xl">
                    {/* Image */}
                    <div className="relative h-48 w-full overflow-hidden">
                      <div
                        className={`absolute inset-0 bg-gradient-to-br ${useCase.gradient} opacity-90`}
                      />
                      <Image
                        src={useCase.image}
                        alt={useCase.title}
                        fill
                        className="object-cover mix-blend-overlay"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <h2 className="text-center text-2xl font-bold text-white drop-shadow-lg">
                          {useCase.title}
                        </h2>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <p className="text-sm text-gray-600">{useCase.description}</p>

                      {/* Key Benefits */}
                      <ul className="mt-4 space-y-2">
                        {useCase.keyBenefits.map((benefit, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <svg
                              className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2.5}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            <span className="text-sm text-gray-700">{benefit}</span>
                          </li>
                        ))}
                      </ul>

                      {/* Metric Badge */}
                      <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-2">
                        <svg
                          className="h-4 w-4 text-primary-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                          />
                        </svg>
                        <span className="text-sm font-semibold text-primary-900">
                          {useCase.metric}
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-50 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Ready to Transform Your Operations?
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Join leading organizations using AI knowledge agents to deliver instant, cited answers
            from their own data.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <Link
              href={PageLink.demo}
              className="inline-flex items-center justify-center rounded-full bg-primary-600 px-8 py-3 text-base font-medium text-white hover:bg-primary-700">
              Book a Demo
            </Link>
            <Link
              href={PageLink.login}
              className="inline-flex items-center justify-center rounded-full border border-gray-300 bg-white px-8 py-3 text-base font-medium text-gray-700 hover:bg-gray-50">
              Start Free Trial
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default UseCases;
